import { Link } from "react-router-dom";
import { FileText, ShieldAlert, CreditCard, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { POLICY_SECTIONS, POLICY_HIGHLIGHTS, LAST_UPDATED } from "@/data/policies";

const HIGHLIGHT_ICONS = [CreditCard, FileText, ShieldAlert];

const Policy = () => (
    <>
        <Seo
            title="Our Policy | Coaching Crafters AI"
            description="Payment terms, refund policy, data privacy, account security, acceptable use, warranty and governing law for Coaching Crafters AI products and services."
            path="/policy"
        />

        <section className="mx-auto max-w-7xl px-6 pt-16 sm:px-10 lg:pt-20" data-testid="policy-hero">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember">Our Policy</p>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                <MaskedLine delay={0.1}>The fine print,</MaskedLine>
                <MaskedLine delay={0.25}>
                    written <em className="font-medium italic text-ember">plainly.</em>
                </MaskedLine>
            </h1>
            <Reveal delay={0.3}>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/60">
                    Coaching Crafters AI is a brand of Sarvacharya Education Pvt Ltd. These policies govern every
                    Coaching Crafters AI product and service — software, ERP, interactive panels and solar systems
                    alike.
                </p>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-ink/40" data-testid="policy-last-updated">
                    Last updated: {LAST_UPDATED}
                </p>
            </Reveal>

            {/* HIGHLIGHTS STRIP */}
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="policy-highlights">
                {POLICY_HIGHLIGHTS.map((h, i) => {
                    const Icon = HIGHLIGHT_ICONS[i];
                    return (
                        <Reveal key={h.title} delay={i * 0.08}>
                            <div className="h-full rounded-2xl border border-ember/25 bg-ember/5 p-6">
                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ember/15">
                                    <Icon className="h-5 w-5 text-ember" />
                                </span>
                                <h2 className="mt-4 text-sm font-bold uppercase tracking-wide text-ink">{h.title}</h2>
                                <p className="mt-2 text-sm leading-relaxed text-ink/60">{h.text}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16 sm:px-10" data-testid="policy-body">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[260px_1fr]">
                {/* STICKY TOC */}
                <aside className="lg:sticky lg:top-28 lg:self-start" data-testid="policy-toc">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink/40">Contents</p>
                    <nav className="mt-4 flex flex-col gap-1">
                        {POLICY_SECTIONS.map((s) => (
                            <Link
                                key={s.id}
                                to={`#${s.id}`}
                                data-testid={`toc-${s.id}`}
                                className="rounded-lg px-3 py-2 text-sm text-ink/60 transition-colors hover:bg-sand hover:text-ember"
                            >
                                <span className="mr-2 font-mono text-xs text-ember">{s.num}.</span>
                                {s.title}
                            </Link>
                        ))}
                    </nav>
                </aside>

                {/* SECTIONS */}
                <div className="space-y-14">
                    {POLICY_SECTIONS.map((s) => (
                        <Reveal key={s.id}>
                            <section id={s.id} data-testid={`policy-section-${s.id}`} className="scroll-mt-20">
                                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                                    <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">
                                        <span className="mr-3 text-ember">{s.num}.</span>
                                        {s.title}
                                    </h2>
                                    {s.standalone && (
                                        <Link
                                            to={s.standalone}
                                            data-testid="policy-privacy-standalone-link"
                                            className="inline-flex items-center gap-1 text-sm font-semibold text-ember hover:underline"
                                        >
                                            Read standalone page <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                    )}
                                </div>
                                <ol className="mt-6 space-y-4">
                                    {s.clauses.map((c, ci) => (
                                        <li key={ci} className="flex gap-4 text-sm leading-relaxed text-ink/70 sm:text-base">
                                            <span className="shrink-0 font-mono text-xs font-semibold text-ember">
                                                {s.num}.{ci + 1}
                                            </span>
                                            <span>{c}</span>
                                        </li>
                                    ))}
                                </ol>
                            </section>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    </>
);

export default Policy;
